/**
 * The main thread's handle on the sim.
 *
 * Everything crossing this file obeys the boundary rules: the renderer asks for
 * a *snapshot*, never an entity; the overlays get a UI store per tick, never a
 * subscription to sim internals; and the UI's only write is a command batch.
 * Which transport is underneath is decided once, reported plainly, and visible
 * in the HUD — because "does the boundary stay quiet under stress" is one of
 * B0's four kill/continue questions, and it has two different answers depending
 * on whether the page got cross-origin isolation.
 */

import { PostCommandSink, SabCommandSink, type CommandSink } from './commands';
import { CopyRingReader, SabRingReader, type Snapshot, type SnapshotSource } from './ring';
import type {
  CampaignCommand,
  CampaignUi,
  Command,
  DesignPreviewUi,
  FloorCommand,
  FloorUi,
  FromWorker,
  Pace,
  ReadyMessage,
  RefusalUi,
  Transport,
  WorkerStats,
} from './protocol';

export interface HostOptions {
  /** The floor's seed, and (M1.1) the economy's RNG seed. */
  seed: number;
  rngSeed: number;
  capacity: number;
  /** The transport to ask for; the worker has the last word. */
  transport: Transport;
}

/**
 * Pick a transport from the page's actual situation, with `?transport=copy` as
 * the override that lets an isolated page still exercise the copy path.
 */
export function preferredTransport(search: string = window.location.search): Transport {
  const forced = new URLSearchParams(search).get('transport');
  if (forced === 'copy' || forced === 'sab') return forced;
  return window.crossOriginIsolated && typeof SharedArrayBuffer !== 'undefined' ? 'sab' : 'copy';
}

export class FloorHost {
  readonly ready: Promise<ReadyMessage>;
  /** Filled in once the worker answers; the HUD reads it directly. */
  info: ReadyMessage | null = null;
  /**
   * Called on every UI-store publish. The overlays' only subscription — the
   * floor's store and, since M1.1, the campaign's beside it.
   */
  onTick: ((ui: FloorUi, campaign: CampaignUi, stats: WorkerStats) => void) | null = null;
  /** Called if the worker dies or reports a boundary error. */
  onError: ((message: string) => void) | null = null;

  private readonly worker: Worker;
  private source: SnapshotSource | null = null;
  private sink: CommandSink | null = null;
  private queued: CampaignCommand[] = [];
  /** Outstanding probe/export questions, by id. */
  private readonly asked = new Map<number, (value: never) => void>();
  private nextAsk = 1;

  constructor(options: HostOptions) {
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.ready = new Promise<ReadyMessage>((resolve, reject) => {
      this.worker.onerror = (event) => {
        const message = event.message || 'worker failed to start';
        this.onError?.(message);
        reject(new Error(message));
      };
      this.worker.onmessage = (event: MessageEvent<FromWorker>) => {
        const message = event.data;
        switch (message.type) {
          case 'ready':
            this.attach(message);
            resolve(message);
            break;
          case 'snapshot':
            if (this.source instanceof CopyRingReader) {
              this.source.accept(message.slot, message.seq, message.bytes);
            }
            break;
          case 'tick':
            this.onTick?.(message.ui, message.campaign, message.stats);
            break;
          case 'probeResult':
            this.answer(message.id, message.refusal);
            break;
          case 'designPreviewResult':
            this.answer(message.id, message.preview);
            break;
          case 'layout':
            this.answer(message.id, message.json);
            break;
          case 'saveBlob':
            this.answer(message.id, message.json);
            break;
          case 'loadResult':
            this.answer(message.id, message.error);
            break;
          case 'error':
            this.onError?.(message.message);
            reject(new Error(message.message));
            break;
        }
      };
    });
    this.worker.postMessage({ type: 'boot', ...options });
  }

  private attach(message: ReadyMessage): void {
    this.info = message;
    if (
      message.transport === 'sab' &&
      message.snapshotControl &&
      message.snapshotRing &&
      message.commandControl &&
      message.commandData
    ) {
      this.source = new SabRingReader(message.layout, message.snapshotControl, message.snapshotRing);
      this.sink = new SabCommandSink(message.commandControl, message.commandData);
    } else {
      this.source = new CopyRingReader(message.layout);
      this.sink = new PostCommandSink(this.worker);
    }
    if (this.queued.length > 0) {
      this.sink.send(this.queued);
      this.queued = [];
    }
  }

  /** The newest published state, or `null` before the first publish lands. */
  latest(): Snapshot | null {
    return this.source?.latest() ?? null;
  }

  /** The one before it — what the renderer interpolates from. */
  previous(): Snapshot | null {
    return this.source?.previous() ?? null;
  }

  publishes(): number {
    return this.source?.publishes() ?? 0;
  }

  transport(): Transport | null {
    return this.source?.transport ?? null;
  }

  droppedCommands(): number {
    return this.sink?.dropped() ?? 0;
  }

  /**
   * Queue floor actions (B0's vocabulary). Sent before boot completes, they
   * wait for it. Wrapped for the campaign's clock-tagged union on the way.
   */
  send(...commands: FloorCommand[]): void {
    this.sendCampaign(...commands.map((command): CampaignCommand => ({ kind: 'floor', command })));
  }

  /** Send economy actions (M0's vocabulary): applied on arrival, paused or not. */
  sendSim(...commands: Command[]): void {
    this.sendCampaign(...commands.map((command): CampaignCommand => ({ kind: 'sim', command })));
  }

  /** The campaign's pace — the one control that belongs to neither clock. */
  setPace(pace: Pace): void {
    this.sendCampaign({ kind: 'setPace', pace });
  }

  setForwardRate(weeksPerSecond: number): void {
    this.sendCampaign({ kind: 'setForwardRate', weeks_per_second: weeksPerSecond });
  }

  /** Queue campaign commands of any kind. */
  sendCampaign(...commands: CampaignCommand[]): void {
    if (this.sink) this.sink.send(commands);
    else this.queued.push(...commands);
  }

  /** The whole campaign as a save document (M1.1). */
  save(): Promise<string> {
    return this.ask<string>((id) => this.worker.postMessage({ type: 'save', id }));
  }

  /** Replace the running campaign with a saved one; resolves to a refusal or `null`. */
  load(json: string): Promise<string | null> {
    return this.ask<string | null>((id) => this.worker.postMessage({ type: 'load', id, json }));
  }

  /**
   * Ask whether an edit would be refused, without making it.
   *
   * This is a *read* across the boundary, and it stays on the right side of the
   * rule that bans per-entity getters: it asks one question about the whole
   * site and gets one answer, from the same legality function the command path
   * runs. The page asks when the cursor changes cell, not per frame — a player
   * action's cadence.
   */
  probe(command: FloorCommand): Promise<RefusalUi | null> {
    return this.ask<RefusalUi | null>((id) =>
      this.worker.postMessage({ type: 'probe', id, json: JSON.stringify(command) }),
    );
  }

  /**
   * The design desk's preview (M1.5): what a loadout would compile to, or the
   * refusal — answered by the same `plan_design` the `StartDesign` command
   * runs, so the form and the tape-out cannot disagree. Asked when the player
   * edits the loadout, never per frame.
   */
  designPreview(ask: {
    generation: string;
    node: string;
    loadout: (number | null)[];
  }): Promise<DesignPreviewUi> {
    return this.ask<DesignPreviewUi>((id) =>
      this.worker.postMessage({ type: 'designPreview', id, json: JSON.stringify(ask) }),
    );
  }

  /** The layout as a JSON file. */
  exportLayout(): Promise<string> {
    return this.ask<string>((id) => this.worker.postMessage({ type: 'export', id }));
  }

  /**
   * One of the build's own layouts, by name — `null` if this build has no such
   * pack. The bench uses it so that the floor it stresses is the same file the
   * harness stresses, rather than a copy that could drift.
   */
  async builtinLayout(name: string): Promise<string | null> {
    const json = await this.ask<string>((id) =>
      this.worker.postMessage({ type: 'builtin', id, name }),
    );
    return json.length > 0 ? json : null;
  }

  private ask<T>(post: (id: number) => void): Promise<T> {
    const id = this.nextAsk++;
    return new Promise<T>((resolve) => {
      this.asked.set(id, resolve as (value: never) => void);
      post(id);
    });
  }

  private answer(id: number, value: unknown): void {
    const resolve = this.asked.get(id);
    if (!resolve) return;
    this.asked.delete(id);
    (resolve as (value: unknown) => void)(value);
  }

  shutdown(): void {
    this.worker.postMessage({ type: 'shutdown' });
    this.worker.terminate();
  }
}

export type { Snapshot, SnapshotSource } from './ring';
export type {
  AssignmentUi,
  GradeSplitUi,
  ProductGradeUi,
  SlotUi,
  BidUi,
  CampaignCommand,
  CampaignUi,
  Command,
  ContractUi,
  EventUi,
  Figure,
  FirmUi,
  LedgerUi,
  LinePointUi,
  LineUi,
  MarketUi,
  NodeUi,
  Pace,
  PnlUi,
  ProductUi,
  ProjectUi,
  RivalUi,
  SocketUi,
  TechUi,
  CalibrationUi,
  CostUi,
  CrewUi,
  CurvePointUi,
  Dispatch,
  DispatchTypeUi,
  FloorCommand,
  FloorUi,
  FloorView,
  FlowUi,
  FrameBudget,
  MoveUi,
  ObjectiveUi,
  ReadingUi,
  RefusalUi,
  RoleLoadUi,
  ScoreUi,
  SessionUi,
  SiteCatalogUi,
  ToolLoadUi,
  ToolModelUi,
  FleetUi,
  MachineUi,
  Purchase,
  UsedListingUi,
  UsedMarketUi,
  DivisionUi,
  LibraryEntryUi,
  TemplateUi,
  GenerationUi,
  GenSlotUi,
  DesignPreviewUi,
  PreviewAttrUi,
  Funding,
  DivisionKind,
  WorkerStats,
  Transport,
} from './protocol';
